import type { Outputs } from "@postdaily/api/routers/index";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { Badge } from "@postdaily/ui/components/badge";
import { Button } from "@postdaily/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@postdaily/ui/components/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@postdaily/ui/components/empty";
import { Input } from "@postdaily/ui/components/input";
import { Label } from "@postdaily/ui/components/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@postdaily/ui/components/radio-group";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { Spinner } from "@postdaily/ui/components/spinner";
import { cn } from "@postdaily/ui/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { UserPlus, X } from "lucide-react";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";

import { MemberTable } from "@/components/members/member-table";
import {
  type InvitableWorkspaceRole,
  invitableRolesForActor,
} from "@/components/members/workspace-roles";
import { PageHeader } from "@/components/page-header";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { track } from "@/lib/analytics";
import { toastMutationError } from "@/lib/mutation-toast";
import { useTRPC } from "@/utils/trpc";

export const Route = createFileRoute("/(app)/settings/members")({
  component: RouteComponent,
});

type Member = Outputs["workspaces"]["members"]["list"][number];
type Role = Exclude<Member["role"], "owner">;

function MembersPageHeader() {
  return (
    <PageHeader
      size="lg"
      title="Members"
      description="Invite teammates and manage roles for this workspace."
    />
  );
}

function RouteComponent() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const viewer = useQuery(trpc.viewer.me.queryOptions());
  const { currentWorkspace, refresh } = useCurrentWorkspace();

  if (!currentWorkspace) {
    return (
      <div className="flex flex-col gap-6">
        <MembersPageHeader />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  const canManage =
    currentWorkspace.role === "owner" || currentWorkspace.role === "admin";

  if (!canManage) {
    return (
      <div className="flex flex-col gap-6">
        <MembersPageHeader />
        <Alert>
          <AlertTitle>Member management unavailable</AlertTitle>
          <AlertDescription>
            Your role can view workspace content but cannot manage members.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <MembersPanel
      currentUserId={viewer.data?.user.id ?? null}
      isViewerLoading={viewer.isPending}
      role={currentWorkspace.role}
      workspaceId={currentWorkspace.id}
      onWorkspaceChanged={refresh}
      queryClient={queryClient}
      trpc={trpc}
    />
  );
}

function MembersPanel({
  currentUserId,
  isViewerLoading,
  onWorkspaceChanged,
  queryClient,
  role,
  trpc,
  workspaceId,
}: {
  currentUserId: string | null;
  isViewerLoading: boolean;
  onWorkspaceChanged: () => void;
  queryClient: ReturnType<typeof useQueryClient>;
  role: Outputs["workspaces"]["list"][number]["role"];
  trpc: ReturnType<typeof useTRPC>;
  workspaceId: string;
}) {
  const membersQuery = useQuery(
    trpc.workspaces.members.list.queryOptions({ workspaceId }),
  );

  async function invalidateMemberData() {
    await Promise.all([
      queryClient.invalidateQueries(
        trpc.workspaces.members.list.queryFilter({ workspaceId }),
      ),
      queryClient.invalidateQueries(trpc.workspaces.list.queryFilter()),
    ]);
    onWorkspaceChanged();
  }

  const updateRole = useMutation(
    trpc.workspaces.members.updateRole.mutationOptions({
      onSuccess: async () => {
        toast.success("Member role updated");
        await invalidateMemberData();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );
  const removeMember = useMutation(
    trpc.workspaces.members.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Member removed");
        await invalidateMemberData();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );
  const transferOwnership = useMutation(
    trpc.workspaces.members.transferOwnership.mutationOptions({
      onSuccess: async () => {
        toast.success("Ownership transferred");
        await invalidateMemberData();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );
  const isMutating =
    updateRole.isPending ||
    removeMember.isPending ||
    transferOwnership.isPending;

  if (membersQuery.isPending || isViewerLoading) {
    return (
      <div className="flex flex-col gap-6">
        <MembersPageHeader />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  if (membersQuery.isError) {
    return (
      <div className="flex flex-col gap-6">
        <MembersPageHeader />
        <Alert variant="destructive">
          <AlertTitle>Members unavailable</AlertTitle>
          <AlertDescription>{membersQuery.error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!currentUserId) {
    return (
      <div className="flex flex-col gap-6">
        <MembersPageHeader />
        <Alert variant="destructive">
          <AlertTitle>Session unavailable</AlertTitle>
          <AlertDescription>
            We could not load your account details for member management.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <MembersPageHeader />
      <InviteMemberCard
        actorRole={role}
        disabled={isMutating}
        queryClient={queryClient}
        trpc={trpc}
        workspaceId={workspaceId}
      />
      <Card>
        <CardHeader className="border-b">
          <CardTitle>Members</CardTitle>
          <CardDescription>
            Review active workspace members and adjust roles.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {membersQuery.data.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>No members found</EmptyTitle>
                <EmptyDescription>
                  This workspace needs a member repair before it can be used.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <MemberTable
              actorRole={role}
              currentUserId={currentUserId}
              disabled={isMutating}
              members={membersQuery.data}
              onRemove={(member: Member) => {
                removeMember.mutate({
                  userId: member.userId,
                  workspaceId,
                });
              }}
              onRoleChange={(member: Member, nextRole: Role) => {
                updateRole.mutate({
                  role: nextRole,
                  userId: member.userId,
                  workspaceId,
                });
              }}
              onTransferOwnership={(member: Member) => {
                transferOwnership.mutate({
                  userId: member.userId,
                  workspaceId,
                });
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function InviteMemberCard({
  actorRole,
  disabled,
  queryClient,
  trpc,
  workspaceId,
}: {
  actorRole: Outputs["workspaces"]["list"][number]["role"];
  disabled: boolean;
  queryClient: ReturnType<typeof useQueryClient>;
  trpc: ReturnType<typeof useTRPC>;
  workspaceId: string;
}) {
  const roleOptions = invitableRolesForActor(actorRole);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InvitableWorkspaceRole>(
    () =>
      roleOptions.find((option) => option.value === "editor")?.value ??
      roleOptions[0]?.value ??
      "editor",
  );

  const invitesQuery = useQuery(
    trpc.workspaces.members.listInvites.queryOptions({ workspaceId }),
  );

  const invalidateInvites = () =>
    queryClient.invalidateQueries(
      trpc.workspaces.members.listInvites.queryFilter({ workspaceId }),
    );

  const inviteMember = useMutation(
    trpc.workspaces.members.invite.mutationOptions({
      onSuccess: async (member) => {
        track("member_invited", { role, source: "settings", count: 1 });
        toast.success(`Invitation sent to ${member.email}`);
        setEmail("");
        await invalidateInvites();
      },
      onError: (error) => {
        toastMutationError(error);
      },
    }),
  );

  const cancelInvite = useMutation(
    trpc.workspaces.members.cancelInvite.mutationOptions({
      onSuccess: async () => {
        toast.success("Invitation cancelled");
        await invalidateInvites();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      return;
    }
    inviteMember.mutate({ email: trimmed, role, workspaceId });
  }

  const invites = invitesQuery.data ?? [];

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle>Invite a teammate</CardTitle>
        <CardDescription>
          {/* Was "Invites go to people who already have a PostDaily account
              with that email" — untrue since invites became token-based: an
              unregistered address gets an email and joins on signup. The copy
              told owners to go chase their teammate into signing up first. */}
          They'll get an email invitation. No PostDaily account needed — they
          can create one when they accept.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                autoComplete="off"
                placeholder="teammate@company.com"
                value={email}
                onChange={(event) => setEmail(event.currentTarget.value)}
              />
            </div>
            <Button
              type="submit"
              disabled={disabled || inviteMember.isPending || !email.trim()}
            >
              {inviteMember.isPending ? (
                <Spinner data-icon="inline-start" />
              ) : (
                <UserPlus data-icon="inline-start" />
              )}
              Send invite
            </Button>
          </div>

          <div className="flex flex-col gap-2">
            <Label id="invite-role-label">Role</Label>
            <RadioGroup
              aria-labelledby="invite-role-label"
              value={role}
              onValueChange={(value) => {
                if (
                  value === "admin" ||
                  value === "editor" ||
                  value === "viewer"
                ) {
                  setRole(value);
                }
              }}
              className="gap-2"
            >
              {roleOptions.map((option) => {
                const selected = role === option.value;
                const optionId = `invite-role-${option.value}`;
                return (
                  <label
                    key={option.value}
                    htmlFor={optionId}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors",
                      selected
                        ? "border-primary bg-primary/5"
                        : "hover:bg-muted/40",
                    )}
                  >
                    <RadioGroupItem
                      id={optionId}
                      value={option.value}
                      className="mt-0.5"
                    />
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="font-medium text-sm leading-none">
                        {option.label}
                      </span>
                      <span className="text-muted-foreground text-sm leading-snug">
                        {option.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </RadioGroup>
          </div>
        </form>

        {invites.length > 0 ? (
          <div className="flex flex-col gap-2">
            <span className="font-medium text-muted-foreground text-sm">
              Pending invitations
            </span>
            {invites.map((invite) => (
              <div
                key={invite.id}
                className="flex items-center gap-3 rounded-lg border p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-sm">{invite.email}</p>
                  <p className="truncate text-muted-foreground text-xs">
                    Invited
                    {invite.invitedByName ? ` by ${invite.invitedByName}` : ""}{" "}
                    · expires {new Date(invite.expiresAt).toLocaleDateString()}
                  </p>
                </div>
                <Badge variant="secondary" className="capitalize">
                  {invite.role}
                </Badge>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={cancelInvite.isPending}
                  onClick={() =>
                    cancelInvite.mutate({ inviteId: invite.id, workspaceId })
                  }
                >
                  <X data-icon="inline-start" />
                  Cancel
                </Button>
              </div>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
